// ═══════════════════════════════════════════════════════════════════════════════
//  src/dojo/dojoBank.js
//  EL DOJO — bancos de contenido de los cuatro formatos de alta intensidad
//
//  ── POR QUÉ ESTE ARCHIVO EXISTE ─────────────────────────────────────────────
//  Los cuatro formatos entrenan el idioma como habilidad refleja, no como
//  conocimiento. Eso obliga a que el contenido sea distinto al del curso: aquí
//  no hay explicaciones, hay estímulos. Frases que se disparan, trampas que se
//  reconocen de oído, líneas que se imitan y crisis que se resuelven.
//
//  ── DOS ETIQUETAS EN CADA REACTIVO ──────────────────────────────────────────
//  `ctx`  → business_tech | travel_social | academic | general
//           Los mismos valores que el onboarding ya captura (Pantalla 3). Hoy
//           el alumno los elige a mano en el Dojo; el día que el perfil de
//           entrenamiento viva en el usuario (Ola 1 de la auditoría), esto se
//           filtra solo.
//  `tag`  → competencia del examen ("Mide tu condición").
//           Permite que el Dojo se conecte después a las fracturas reales del
//           alumno en vez de servir contenido al azar.
//
//  Nada aquí llama a la IA ni al backend: es contenido estático, viaja en el
//  bundle y funciona sin conexión. La única excepción es Crisis Room, que sí
//  necesita juicio de IA para evaluar la respuesta.
// ═══════════════════════════════════════════════════════════════════════════════

export const CONTEXTS = [
  { id: 'general', label: 'Todo', icon: '🌐' },
  { id: 'business_tech', label: 'Trabajo', icon: '💼' },
  { id: 'travel_social', label: 'Social', icon: '✈️' },
  { id: 'academic', label: 'Académico', icon: '🎓' },
];

/* ═══════════════════════════════════════════════════════════════════════════
   1. SPARRING DE REFLEJOS
   ---------------------------------------------------------------------------
   Spacy lanza una frase y aparecen DOS respuestas. Cuatro segundos para elegir.

   ── POR QUÉ DOS OPCIONES Y NO VOZ LIBRE ────────────────────────────────────
   La primera versión pedía responder hablando. El formato era correcto, la
   medición no: a "your mic is muted" hay cientos de respuestas buenas, y el
   sistema sólo daba por válidas las de su lista. Castigaba respuestas
   correctas, que es la peor cosa que puede hacerle un entrenador a un alumno.

   Con dos opciones el reflejo se sigue entrenando —hay que decidir antes de
   poder traducir— pero el juicio es exacto: una está bien y la otra no. Y de
   paso el formato deja de necesitar micrófono: funciona en Safari, sin
   permisos y sin ruido de fondo.

   `bad` nunca es un disparate: es lo que un hispanohablante diría de verdad.
   Ahí está el valor — el alumno se reconoce en la opción equivocada.
   ═══════════════════════════════════════════════════════════════════════════ */

export const SPARRING = [
  {
    id: 'sp-01', ctx: 'business_tech', tag: 'functional_language',
    say: 'Hey, your mic is muted!',
    hint: 'Te avisan en plena junta',
    good: 'Sorry! Can you hear me now?',
    bad: 'Yes, I am muted.',
    why: 'Confirmar el problema no lo resuelve. Se reconoce y se comprueba: "Can you hear me now?".',
  },
  {
    id: 'sp-02', ctx: 'business_tech', tag: 'functional_language',
    say: 'Can you walk me through this?',
    hint: 'Tu jefe, sobre tu reporte',
    good: 'Sure — let me start from the top.',
    bad: 'Yes, I walk you through it.',
    why: 'Repetir el phrasal verb suena a traducción. Se contesta con "Sure" y lo que vas a hacer.',
  },
  {
    id: 'sp-03', ctx: 'business_tech', tag: 'hedging',
    say: 'So, are we launching on Friday?',
    hint: 'Y todavía no lo sabes',
    good: "I'm not sure yet — let me check and confirm.",
    bad: 'I not sure. Maybe yes, maybe not.',
    why: 'Falta el verbo: "I am not sure". Y "maybe yes, maybe not" te deja sin credibilidad.',
  },
  {
    id: 'sp-04', ctx: 'business_tech', tag: 'diplomatic_language',
    say: 'I think your estimate is way too high.',
    hint: 'Y no estás de acuerdo',
    good: 'I see your point — let me explain how I got there.',
    bad: 'I am agree, but no.',
    why: '"Agree" es verbo: se dice "I agree". Y contradecirte en la misma frase te deja sin postura.',
  },
  {
    id: 'sp-05', ctx: 'business_tech', tag: 'functional_language',
    say: "Sorry, I didn't catch that.",
    hint: 'Te piden repetir',
    good: 'No problem — I said we need two more days.',
    bad: 'Repeat again? I said two days more.',
    why: 'Te pidieron a ti que repitas, no al revés. Y el orden correcto es "two more days".',
  },
  {
    id: 'sp-06', ctx: 'business_tech', tag: 'register',
    say: 'Quick one — do you have a minute?',
    hint: 'Te interceptan y estás ocupado',
    good: "Give me five minutes and I'm all yours.",
    bad: 'I have not time right now.',
    why: 'El auxiliar no se salta: "I do not have time". Y cerrar sin alternativa cierra la puerta.',
  },
  {
    id: 'sp-07', ctx: 'travel_social', tag: 'tone_conversational',
    say: "Hey! Long time no see. How've you been?",
    hint: 'Te encuentras a alguien en la calle',
    good: 'Pretty good, actually! Busy with work. You?',
    bad: 'I have been fine since long time.',
    why: '"Since" pide un punto de partida, no una duración: sería "for a long time".',
  },
  {
    id: 'sp-08', ctx: 'travel_social', tag: 'functional_language',
    say: 'Are you ready to order?',
    hint: 'El mesero, y no has decidido',
    good: 'Almost — could you give me one more minute?',
    bad: 'Not yet. I want think more.',
    why: 'Después de "want" nunca se pierde el "to": "I want to think".',
  },
  {
    id: 'sp-09', ctx: 'travel_social', tag: 'functional_language',
    say: "That'll be twenty-four fifty.",
    hint: 'Pagando, y sólo traes tarjeta',
    good: 'Do you take card?',
    bad: 'I pay with card, no?',
    why: 'Ni pregunta ni afirma. La fórmula fija es "Do you take card?".',
  },
  {
    id: 'sp-10', ctx: 'travel_social', tag: 'tone_conversational',
    say: 'Oh no, my flight just got cancelled.',
    hint: 'Un amigo, desde el aeropuerto',
    good: 'Oh no, that sucks. What are you gonna do?',
    bad: 'What a pity. I feel sorry for you.',
    why: 'Las dos frases existen y ninguna se usa entre amigos: suenan a subtítulo viejo y a distancia.',
  },
  {
    id: 'sp-11', ctx: 'travel_social', tag: 'verb_to_be',
    say: 'Excuse me — is this seat taken?',
    hint: 'En el tren',
    good: "No, go ahead — it's all yours.",
    bad: 'No, is free. Sit down.',
    why: 'Falta el sujeto ("It is free"), y "Sit down" en inglés suena a orden, no a invitación.',
  },
  {
    id: 'sp-12', ctx: 'travel_social', tag: 'present_perfect',
    say: 'How long have you been here?',
    hint: 'En una fiesta',
    good: 'About twenty minutes — I just got here.',
    bad: 'I am here since twenty minutes.',
    why: 'Duración = present perfect + "for": "I have been here for twenty minutes".',
  },
  {
    id: 'sp-13', ctx: 'academic', tag: 'argument_structure',
    say: "What's your position on this?",
    hint: 'En el seminario',
    good: "I'd argue the opposite, actually — and here's why.",
    bad: 'For me, is a difficult question.',
    why: '"For me" es calco de "para mí"; se dice "In my view". Y falta el sujeto "it".',
  },
  {
    id: 'sp-14', ctx: 'academic', tag: 'hedging',
    say: 'Does the data support that claim?',
    hint: 'Y no del todo',
    good: "To some extent — the data suggests it, but it isn't conclusive.",
    bad: 'It depends of the interpretation.',
    why: 'Es "depend on", nunca "depend of". Uno de los calcos que más delatan.',
  },
  {
    id: 'sp-15', ctx: 'academic', tag: 'countable_uncountable',
    say: 'Could you clarify your methodology?',
    hint: 'Defendiendo tu trabajo',
    good: 'Certainly — we used a mixed-methods approach.',
    bad: 'Yes, I did a research with two methods.',
    why: '"Research" es incontable: "I did research". Nunca lleva artículo ni plural.',
  },
  {
    id: 'sp-16', ctx: 'general', tag: 'verb_to_be',
    say: 'Wait — did you say Tuesday or Thursday?',
    hint: 'Confirmando una cita',
    good: 'Thursday — the day after tomorrow.',
    bad: 'Is Thursday, sorry for the confusion.',
    why: 'Falta el sujeto: "It is Thursday". En inglés el sujeto no se omite nunca.',
  },
  {
    id: 'sp-17', ctx: 'general', tag: 'functional_language',
    say: 'Honestly, I completely forgot.',
    hint: 'Alguien te falló y se disculpa',
    good: 'No worries — it happens.',
    bad: "Don't worry, is not important for me.",
    why: 'Falta el sujeto, y "for me" minimiza algo que a la otra persona sí le importó.',
  },
  {
    id: 'sp-18', ctx: 'general', tag: 'verb_to_be',
    say: 'So... tell me about yourself.',
    hint: 'La pregunta más temida',
    good: "Sure — I'm a project manager, currently working in fintech.",
    bad: 'I have 30 years and I work in fintech.',
    why: 'La edad va con "to be": "I am 30". Con "have" es traducción directa del español.',
  },
];

/* ═══════════════════════════════════════════════════════════════════════════
   2. SWIPE DE SUPERVIVENCIA
   ---------------------------------------------------------------------------
   Derecha = suena bien.  Izquierda = está mal.

   Todas las trampas son errores REALES de hispanohablante: calcos de estructura
   del español, no errores aleatorios. Ese es el punto — se entrena el oído a
   detectar el patrón propio, no gramática genérica.
   ═══════════════════════════════════════════════════════════════════════════ */

export const SWIPE = [
  // ── Trabajo ──────────────────────────────────────────────────────────────
  { id: 'sw-01', ctx: 'business_tech', tag: 'vocabulary_in_context', ok: true,  text: 'I do business with them.', note: 'Con “business” siempre es “do”.' },
  { id: 'sw-02', ctx: 'business_tech', tag: 'vocabulary_in_context', ok: false, text: 'I make business with them.', fix: 'I do business with them.', note: 'Calco de “hacer negocios”. En inglés, do business.' },
  { id: 'sw-03', ctx: 'business_tech', tag: 'present_perfect', ok: true,  text: "I've worked here for three years.", note: 'Empezó antes y sigue: present perfect.' },
  { id: 'sw-04', ctx: 'business_tech', tag: 'present_perfect', ok: false, text: 'I work here since three years.', fix: "I've worked here for three years.", note: '“Desde hace” no se traduce literal. For + duración.' },
  { id: 'sw-05', ctx: 'business_tech', tag: 'third_person_s', ok: false, text: 'She work in the marketing team.', fix: 'She works in the marketing team.', note: 'Tercera persona: la -s no es opcional.' },
  { id: 'sw-06', ctx: 'business_tech', tag: 'countable_uncountable', ok: false, text: 'I need more informations.', fix: 'I need more information.', note: '“Information” es incontable. Nunca lleva -s.' },
  { id: 'sw-07', ctx: 'business_tech', tag: 'countable_uncountable', ok: true,  text: 'We have a lot of feedback on this.', note: '“Feedback” también es incontable.' },
  { id: 'sw-08', ctx: 'business_tech', tag: 'word_order_svo', ok: false, text: 'I want to know what do you think.', fix: 'I want to know what you think.', note: 'Pregunta indirecta: se pierde el auxiliar.' },
  { id: 'sw-09', ctx: 'business_tech', tag: 'modals_deduction', ok: true,  text: 'The server must be down.', note: 'Deducción con certeza: must be.' },
  { id: 'sw-10', ctx: 'business_tech', tag: 'passive_voice', ok: true,  text: 'The report was sent yesterday.', note: 'Pasiva limpia: importa el reporte, no quién.' },
  { id: 'sw-11', ctx: 'business_tech', tag: 'passive_voice', ok: false, text: 'The report was send yesterday.', fix: 'The report was sent yesterday.', note: 'La pasiva pide participio: sent, no send.' },
  { id: 'sw-12', ctx: 'business_tech', tag: 'register', ok: false, text: 'I am agree with the proposal.', fix: 'I agree with the proposal.', note: '“Agree” es verbo. “Estoy de acuerdo” engaña.' },
  { id: 'sw-13', ctx: 'business_tech', tag: 'phrasal_verbs', ok: true,  text: 'Let me follow up on that.', note: 'Phrasal verb de oficina, imprescindible.' },
  { id: 'sw-14', ctx: 'business_tech', tag: 'prepositions', ok: false, text: 'We depend of the client approval.', fix: "We depend on the client's approval.", note: '“Depend on”. Y el posesivo pide apóstrofo.' },

  // ── Social / viajes ──────────────────────────────────────────────────────
  { id: 'sw-15', ctx: 'travel_social', tag: 'verb_to_be', ok: false, text: 'I have 30 years old.', fix: "I'm 30 years old.", note: 'La edad va con “to be”, no con “have”.' },
  { id: 'sw-16', ctx: 'travel_social', tag: 'verb_to_be', ok: true,  text: "I'm really hungry.", note: 'Estados con “to be”: hungry, thirsty, cold.' },
  { id: 'sw-17', ctx: 'travel_social', tag: 'verb_to_be', ok: false, text: 'I have cold, can we go inside?', fix: "I'm cold, can we go inside?", note: '“Tengo frío” = I’m cold.' },
  { id: 'sw-18', ctx: 'travel_social', tag: 'past_simple_irregular', ok: false, text: 'I goed to the beach last weekend.', fix: 'I went to the beach last weekend.', note: 'Go es irregular: went.' },
  { id: 'sw-19', ctx: 'travel_social', tag: 'past_simple_irregular', ok: true,  text: 'We ate at that place you mentioned.', note: 'Eat → ate. Irregular bien puesto.' },
  { id: 'sw-20', ctx: 'travel_social', tag: 'word_order_svo', ok: false, text: 'I like very much this city.', fix: 'I really like this city.', note: 'El “very much” no va en medio. Suena traducido.' },
  { id: 'sw-21', ctx: 'travel_social', tag: 'articles', ok: false, text: 'She is engineer.', fix: 'She is an engineer.', note: 'Las profesiones llevan artículo en inglés.' },
  { id: 'sw-22', ctx: 'travel_social', tag: 'articles', ok: true,  text: "He's a doctor at the city hospital.", note: 'Artículo delante de la profesión.' },
  { id: 'sw-23', ctx: 'travel_social', tag: 'present_continuous', ok: false, text: 'I am agree we go now.', fix: "I agree — let's go now.", note: 'Dos errores: “am agree” y la propuesta sin “let’s”.' },
  { id: 'sw-24', ctx: 'travel_social', tag: 'phrasal_verbs', ok: true,  text: "I'm looking forward to it.", note: 'Fórmula fija. Después de “to” va gerundio o pronombre.' },
  { id: 'sw-25', ctx: 'travel_social', tag: 'prepositions', ok: false, text: "We arrived to the airport at 6.", fix: 'We arrived at the airport at 6.', note: '“Arrive at” un lugar puntual, “arrive in” una ciudad.' },
  { id: 'sw-26', ctx: 'travel_social', tag: 'comparatives', ok: false, text: 'This place is more cheap than the other.', fix: 'This place is cheaper than the other.', note: 'Adjetivo corto: -er. No lleva “more”.' },
  { id: 'sw-27', ctx: 'travel_social', tag: 'comparatives', ok: true,  text: 'This one is more expensive than I expected.', note: 'Adjetivo largo: sí lleva “more”.' },
  { id: 'sw-28', ctx: 'travel_social', tag: 'object_pronouns', ok: false, text: 'I told to my friend the news.', fix: 'I told my friend the news.', note: '“Tell” no lleva “to” antes de la persona.' },

  // ── Académico / general ──────────────────────────────────────────────────
  { id: 'sw-29', ctx: 'academic', tag: 'countable_uncountable', ok: false, text: 'I did an important research.', fix: 'I did important research.', note: '“Research” es incontable: sin artículo, sin -s.' },
  { id: 'sw-30', ctx: 'academic', tag: 'word_order_svo', ok: false, text: 'The people is very interested.', fix: 'The people are very interested.', note: '“People” es plural: are.' },
  { id: 'sw-31', ctx: 'academic', tag: 'cohesion', ok: true,  text: 'The results were mixed; however, the trend is clear.', note: 'Conector concesivo bien puntuado.' },
  { id: 'sw-32', ctx: 'academic', tag: 'cohesion', ok: false, text: 'The results were mixed, however the trend is clear.', fix: 'The results were mixed; however, the trend is clear.', note: '“However” pide punto y coma o punto, no coma sola.' },
  { id: 'sw-33', ctx: 'academic', tag: 'conditional_2', ok: false, text: 'If I would have more time, I would finish it.', fix: 'If I had more time, I would finish it.', note: 'En el “if” no va “would”. Es el error más común.' },
  { id: 'sw-34', ctx: 'academic', tag: 'conditional_2', ok: true,  text: 'If I had more data, I could prove it.', note: 'Condicional 2 limpio: past simple + could.' },
  { id: 'sw-35', ctx: 'general', tag: 'present_perfect', ok: true,  text: "I've already sent it.", note: '“Already” dentro del present perfect.' },
  { id: 'sw-36', ctx: 'general', tag: 'third_person_s', ok: false, text: 'My brother live in Madrid.', fix: 'My brother lives in Madrid.', note: 'Tercera persona otra vez. Es la fractura más fosilizada.' },
];

/* ═══════════════════════════════════════════════════════════════════════════
   3. SHADOWING A CIEGAS
   ---------------------------------------------------------------------------
   `seconds` es la duración objetivo de la línea leída a velocidad nativa. Se
   usa para puntuar el RITMO: hablar demasiado lento es el síntoma número uno
   de estar traduciendo en la cabeza.

   `linking` señala las uniones que hacen que suene natural. Se revelan al
   terminar, nunca antes: el punto es imitar de oído, no leer.
   ═══════════════════════════════════════════════════════════════════════════ */

export const SHADOWING = [
  {
    id: 'sh-01', ctx: 'business_tech', tag: 'fluency', seconds: 3.6,
    text: "I'll get back to you on that by the end of the day.",
    linking: ['get back to you → “getbacktoya”', 'end of the day → “endathaday”'],
  },
  {
    id: 'sh-02', ctx: 'business_tech', tag: 'fluency', seconds: 3.4,
    text: "Let's take a step back and look at the bigger picture.",
    linking: ["Let's take a → “letstakea”", 'look at the → “lookatthe”'],
  },
  {
    id: 'sh-03', ctx: 'business_tech', tag: 'business_context', seconds: 4.0,
    text: "What I'm hearing is that the deadline isn't realistic anymore.",
    linking: ["What I'm → “whadai’m”", "isn't realistic → “iznrealistic”"],
  },
  {
    id: 'sh-04', ctx: 'business_tech', tag: 'diplomatic_language', seconds: 4.2,
    text: "I'd rather we didn't commit to a date until we've checked the data.",
    linking: ["I'd rather we → “aidratherwe”", "we've checked → “weevchecked”"],
  },
  {
    id: 'sh-05', ctx: 'travel_social', tag: 'tone_conversational', seconds: 3.0,
    text: "I've been meaning to ask you about that.",
    linking: ["I've been → “aivbin”", 'meaning to → “meaningta”'],
  },
  {
    id: 'sh-06', ctx: 'travel_social', tag: 'tone_conversational', seconds: 3.2,
    text: "You wanna grab a coffee before the meeting?",
    linking: ['wanna = want to', 'grab a → “graba”'],
  },
  {
    id: 'sh-07', ctx: 'travel_social', tag: 'fluency', seconds: 3.4,
    text: "It's not a big deal, don't worry about it.",
    linking: ["It's not a → “itsnota”", "worry about it → “worryaboudit”"],
  },
  {
    id: 'sh-08', ctx: 'academic', tag: 'argument_structure', seconds: 4.4,
    text: "The evidence suggests a correlation, but not necessarily a cause.",
    linking: ['suggests a → “suggestsa”', 'not necessarily → “nonnecessarily”'],
  },
  {
    id: 'sh-09', ctx: 'academic', tag: 'formal_register', seconds: 4.2,
    text: "To what extent does this finding apply outside the sample?",
    linking: ['To what extent → “tawhadextent”', 'apply outside → “applyoutside”'],
  },
  {
    id: 'sh-10', ctx: 'general', tag: 'fluency', seconds: 3.0,
    text: "I should've told you earlier, sorry about that.",
    linking: ["should've → “shudov”", 'sorry about that → “sorryaboutthat”'],
  },
];

/* ═══════════════════════════════════════════════════════════════════════════
   4. CRISIS ROOM
   ---------------------------------------------------------------------------
   45 segundos, una situación de presión real, y una evaluación de jefe — no
   de maestro. La IA juzga si LOGRASTE LA MISIÓN antes de mirar la gramática.

   `mission` es lo que se tiene que conseguir. Va al prompt del backend, así
   que es la vara con la que se mide — no un adorno de la tarjeta.
   ═══════════════════════════════════════════════════════════════════════════ */

export const CRISIS = [
  {
    id: 'cr-01', ctx: 'business_tech', tag: 'business_context', seconds: 45,
    role: 'Líder técnico',
    scene: 'El servidor de base de datos se cayó. Se perdió información de las últimas 4 horas. El cliente está en la línea, enojado, y quiere explicaciones AHORA.',
    mission: 'Reconocer el problema sin culpar a nadie, dar un plazo concreto y mantener la autoridad.',
  },
  {
    id: 'cr-02', ctx: 'business_tech', tag: 'diplomatic_language', seconds: 45,
    role: 'Project manager',
    scene: 'Tu equipo no va a llegar a la fecha de entrega. Faltan dos semanas y el sponsor acaba de entrar a tu oficina a preguntar si vamos bien.',
    mission: 'Decir la verdad antes de que la descubran, y salir con un plan aceptado.',
  },
  {
    id: 'cr-03', ctx: 'business_tech', tag: 'register', seconds: 45,
    role: 'Candidato',
    scene: 'Entrevista final. El director te dice: “Tu perfil es bueno, pero pides 30% más de lo que teníamos presupuestado.”',
    mission: 'Defender tu número con valor demostrable, sin sonar arrogante ni ceder de inmediato.',
  },
  {
    id: 'cr-04', ctx: 'business_tech', tag: 'hedging', seconds: 40,
    role: 'Consultor',
    scene: 'En plena junta, el cliente propone una solución que sabes que va a fallar. Su equipo ya está asintiendo.',
    mission: 'Frenar la decisión sin dejar a nadie en ridículo delante de su equipo.',
  },
  {
    id: 'cr-05', ctx: 'travel_social', tag: 'functional_language', seconds: 40,
    role: 'Pasajero',
    scene: 'Perdiste tu conexión por un retraso de la aerolínea. En el mostrador te dicen que el siguiente vuelo es en 14 horas y que no cubren hotel.',
    mission: 'Conseguir una solución mejor sin perder el trato civilizado.',
  },
  {
    id: 'cr-06', ctx: 'travel_social', tag: 'tone_conversational', seconds: 40,
    role: 'Invitado',
    scene: 'Estás en una cena con los amigos de tu pareja. Alguien te pregunta algo, no entendiste nada, y todos te están mirando.',
    mission: 'Salir del bache sin fingir que entendiste y sin matar la conversación.',
  },
  {
    id: 'cr-07', ctx: 'academic', tag: 'argument_structure', seconds: 45,
    role: 'Ponente',
    scene: 'Terminas tu presentación y un profesor levanta la mano: “Su muestra es demasiado pequeña para sostener esa conclusión.”',
    mission: 'Reconocer el límite real de tu estudio y defender lo que sí sostiene.',
  },
  {
    id: 'cr-08', ctx: 'academic', tag: 'formal_register', seconds: 45,
    role: 'Solicitante',
    scene: 'Comité de admisión. Te preguntan por qué tu promedio bajó en el tercer año, justo el que más pesa.',
    mission: 'Explicar sin excusarte, y convertir el bache en evidencia de algo.',
  },
];

/* ═══════════════════════════════════════════════════════════════════════════
   UTILIDADES
   ═══════════════════════════════════════════════════════════════════════════ */

/** Filtra por contexto. `general` no filtra: devuelve todo. */
export function byContext(bank, ctx) {
  if (!ctx || ctx === 'general') return bank;
  const hit = bank.filter((x) => x.ctx === ctx);
  // Nunca se devuelve vacío: un banco corto es mejor que una pantalla muerta.
  return hit.length >= 4 ? hit : bank;
}

/** Baraja sin mutar el original (Fisher-Yates). */
export function shuffle(list) {
  const out = [...list];
  for (let i = out.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [out[i], out[j]] = [out[j], out[i]];
  }
  return out;
}

/** Normaliza para comparar lo que dijo el alumno contra lo esperado. */
export function normalize(text) {
  return (text || '')
    .toLowerCase()
    .replace(/[’']/g, "'")
    .replace(/[^a-z0-9'\s]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}
